import * as Popover from '@radix-ui/react-popover';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { BUTTON_SIZES, GhostLinkButton } from 'ui/button';
import { useDropdownPopoverAlignment } from 'ui/utility/hooks/useDropdownPopoverAlignment';
import { HORIZONTAL_ALIGNMENT, VERTICAL_ALIGNMENT } from 'ui/utility/types';
import { createDropdownToggle } from 'ui/selects/utils';
import { spacing } from 'ui/theme';
import noop from 'utils/noop';
import {
  DateAndCalendarWrapper,
  RowWrapper,
  StyledDatePickerInput,
  PopoverTriggerWrapper,
} from './DatePicker.styles';
import { SingleDatePicker } from './SingleDatePicker';
import { DateInput } from './datePickerInput/DateInput';
import { RelativeDateInput } from './datePickerInput/RelativeDateInput';
import { TimeInput } from './datePickerInput/TimeInput';
import { NULL_DATE_PLACEHOLDER, RELATIVE_DATE_PLACEHOLDER } from './helpers';
import { DATE_PICKER_PORTAL_ID } from './constants';
import type { HorizontalAlignmentValue, VerticalAlignmentValue } from './types';

const StyledInputBox = styled(StyledDatePickerInput)`
  && {
    padding-right: ${spacing.threeQuarter};
    padding-left: ${spacing.threeQuarter};
  }
`;

type dateValue = Moment | string;

type SingleDatePickerWithCollapsibleCalendarProps = {
  /**
   * Automatically change the dropdown `verticalAlignment` and `horizontalAlignment`
   * to avoid collisions with the viewport.
   * When this is on, `verticalAlignment` and `horizontalAlignment` are used as default alignments.
   */
  automaticallyAvoidCollisions?: boolean;
  /** Whether set date can be cleared */
  canClearDate?: boolean;
  /** Date to display in input and calendar */
  date: dateValue;
  /** Custom/override date placeholder text */
  datePlaceholder?: string;
  /** Whether input value has error */
  hasError?: boolean;
  /** alignment of calender */
  horizontalAlignment?: HorizontalAlignmentValue;
  /** Applies disabled styles for a disabled input */
  isDisabled?: boolean;
  /** Function run when date is changed, either in date input or calendar */
  onDateChange: (date: dateValue) => void;
  /** Custom/override relative date placeholder text */
  relativeDatePlaceholder?: string;
  /** True if time input and time zone picker are shown */
  showTime?: boolean;
  /** whether the date picker has a relative date option: defaults to true */
  useRelativeDate?: boolean;
  /** vertical alignment of calender dropdown */
  verticalAlignment?: VerticalAlignmentValue;
};

export const SingleDatePickerWithCollapsibleCalendar = ({
  automaticallyAvoidCollisions = true,
  canClearDate = false,
  date,
  datePlaceholder = NULL_DATE_PLACEHOLDER,
  hasError = false,
  horizontalAlignment = 'left',
  isDisabled = false,
  onDateChange = noop,
  relativeDatePlaceholder = RELATIVE_DATE_PLACEHOLDER,
  showTime = false,
  useRelativeDate = true,
  verticalAlignment = 'down',
  ...props
}: SingleDatePickerWithCollapsibleCalendarProps) => {
  const [currentDate, setCurrentDate] = useState(date);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isRelative, setIsRelative] = useState(typeof currentDate === 'string');

  const toggleDropdown = createDropdownToggle(setIsDatePickerOpen, isDatePickerOpen);

  const handleSetDate = (newDate: dateValue) => {
    setCurrentDate(newDate);
    onDateChange(newDate);
  };

  useEffect(() => {
    if (isDatePickerOpen) {
      // reset date when switching between relative and non-relative time
      handleSetDate(isRelative ? '' : moment());
    }
  }, [isRelative]);

  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    horizontalAlignment as HORIZONTAL_ALIGNMENT,
    verticalAlignment as VERTICAL_ALIGNMENT,
  );

  return (
    <Popover.Root open={isDatePickerOpen} onOpenChange={toggleDropdown}>
      <DateAndCalendarWrapper {...props}>
        <RowWrapper>
          <Popover.Trigger
            asChild
            onClick={(e) => {
              e.preventDefault();
              if (!isDisabled) {
                toggleDropdown(!!e);
              }
            }}
          >
            <PopoverTriggerWrapper>
              {isRelative ? (
                <StyledDatePickerInput>
                  <RelativeDateInput
                    date={currentDate as string}
                    id="relativeDate-input"
                    isReadOnly
                    isDisabled={isDisabled}
                    placeholder={relativeDatePlaceholder}
                    hasError={hasError}
                  />
                </StyledDatePickerInput>
              ) : (
                <StyledInputBox>
                  <DateInput
                    date={currentDate}
                    id="date-input"
                    isReadOnly
                    isDisabled={isDisabled}
                    placeholder={datePlaceholder}
                  />
                </StyledInputBox>
              )}

              {showTime && !isRelative && (
                <StyledInputBox>
                  <TimeInput
                    id="time-input"
                    isDisabled={isDisabled}
                    isReadOnly={isDisabled}
                    onTimeChange={(newDate: dateValue) => handleSetDate(newDate)}
                    time={currentDate}
                  />
                </StyledInputBox>
              )}
            </PopoverTriggerWrapper>
          </Popover.Trigger>
          {canClearDate && currentDate && (
            <GhostLinkButton onClick={() => handleSetDate('')} size={BUTTON_SIZES.SMALL}>
              Clear
            </GhostLinkButton>
          )}
        </RowWrapper>
        <Popover.Portal>
          <Popover.Content
            data-portal-id={DATE_PICKER_PORTAL_ID}
            hideWhenDetached // automatically hide the content when the trigger becomes fully occluded.
            avoidCollisions={automaticallyAvoidCollisions}
            side={popoverVerticalAlignment}
            align={popoverHorizontalAlignment}
            sideOffset={4} // space between the dropdown and the dropdown trigger
          >
            <SingleDatePicker
              date={currentDate}
              onDateChange={(newDate) => handleSetDate(newDate)}
              useRelativeDate={useRelativeDate}
              isRelativeDate={isRelative}
              onRelativeDateToggle={setIsRelative}
              hasError={hasError}
            />
          </Popover.Content>
        </Popover.Portal>
      </DateAndCalendarWrapper>
    </Popover.Root>
  );
};
